<template>
  <div id="user-list">
    <v-row class="mb-5"></v-row>

    <!-- list filters -->
    <v-card>
      <v-card-title>
        <v-row>
          <v-col>
            Search &amp; Filter
          </v-col>

          <v-col
            align="end"
            class="pb-0"
          >
            <v-tooltip
              v-if="$can('create', 'Event')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  fab
                  x-small
                  elevation="4"
                  color="primary"
                  :to="{ name: 'event-create' }"
                  v-on="on"
                >
                  <v-icon>
                    {{ icons.mdiPlus }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Add New Event</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-title>

      <!-- table -->
      <vuex-table
        :headers="tableColumns"
        :items="events"
        :options="options"
        :search.sync="search"
        :meta.sync="meta"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [5, 10, 25, 50, 100]
        }"
        @update:options="o => options = o"
      >
        <!-- name -->
        <template #[`item.title`]="{item}">
          <div class="d-flex align-center">
            <v-avatar
              :color="item.thumbUrl ? '' : 'primary'"
              :class="item.thumbUrl ? '' : 'v-avatar-light-bg primary--text'"
              size="32"
            >
              <v-img
                v-if="item.thumbUrl"
                :src="item.thumbUrl"
              ></v-img>
              <span
                v-else
                class="font-weight-medium"
              >{{ avatarText(item.title) }}</span>
            </v-avatar>

            <div class="d-flex flex-column ms-3">
              <router-link
                :to="{ name : 'event-view', params : { id : item.id } }"
                class="text--primary font-weight-semibold text-truncate cursor-pointer text-decoration-none"
              >
                {{ item.title | textEllipsis(100) }}
              </router-link>
              <small>{{ item.subtitle | textEllipsis(100) }}</small>
            </div>
          </div>
        </template>

        <!-- actions -->
        <template #[`item.actions`]="{item}">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="me-1"
                v-bind="attrs"
                :to="{ name: 'event-view', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiEyeOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>View Event</span>
          </v-tooltip>

          <v-tooltip
            v-if="$can('update', 'Event')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="me-1"
                v-bind="attrs"
                :to="{ name: 'event-edit', params: { id: item.id } }"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiPencil }}
                </v-icon>
              </v-btn>
            </template>
            <span>Edit Event</span>
          </v-tooltip>

          <v-tooltip
            v-if="$can('destroy', 'Event')"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                v-bind="attrs"
                :disabled="deleting"
                :loading="isDialogOpen"
                @click="selectEventToDelete(item)"
                v-on="on"
              >
                <v-icon size="18">
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </template>
            <span>Delete Event</span>
          </v-tooltip>
        </template>
      </vuex-table>
    </v-card>

    <event-delete-dialog
      v-if="$can('destroy', 'Event')"
      :data="selectedEvent"
      :deleting="deleting"
      :is-dialog-open.sync="isDialogOpen"
    ></event-delete-dialog>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { mdiDotsVertical, mdiEyeOutline, mdiPlus, mdiPencil, mdiDeleteOutline, mdiExportVariant } from '@mdi/js'

// eslint-disable-next-line object-curly-newline
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from '@vue/composition-api'

import { avatarText } from '@core/utils/filter'
import useEventsList from './useEventsList'
import EventDeleteDialog from './EventDeleteDialog.vue'

export default {
  components: {
    EventDeleteDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const isDialogOpen = ref(false)

    const deleting = computed(() => vm.$store.getters['events/deleting'])
    const selectedEvent = ref(null)
    const selectEventToDelete = event => {
      selectedEvent.value = event
      isDialogOpen.value = !!event
    }

    const {
      eventListTable,
      tableColumns,
      totalEventListTable,
      loading,

      eventTotalLocal,

      meta,
      filters,
      search,
      options,
      roleFilter,

      setOptions,
      setFilters,

      events,

      init,
      destroy,
    } = useEventsList()

    onMounted(() => {
      init()
    })
    onUnmounted(() => {
      destroy()
    })

    return {
      eventListTable,
      tableColumns,
      totalEventListTable,

      loading,
      eventTotalLocal,
      avatarText,

      setOptions,
      setFilters,

      events,
      meta,
      filters,
      search,
      options,
      roleFilter,

      // icons
      icons: {
        mdiPlus,
        mdiPencil,
        mdiEyeOutline,
        mdiDotsVertical,
        mdiDeleteOutline,
        mdiExportVariant,
      },

      isDialogOpen,

      deleting,
      selectedEvent,
      selectEventToDelete,
    }
  },
}
</script>

<style lang="scss">
@import '@core/preset/preset/apps/user.scss';
</style>
